<script setup lang="ts">
import { nextTick, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SchoolGradeModifierSymbol from './SchoolGradeModifierSymbol.vue';
import popoverPointer from '@/assets/school-grades/popover-pointer.svg';
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import { EPosition } from 'common/enum/misc.enum';
import { getPopoverArrowStyle } from '@/utils/dom.utils';

const props = defineProps<{
  anchorElement: HTMLButtonElement | null;
  modifier: -1 | 0 | 1;
  decimalMode: boolean;
  minusDisabled: boolean;
  plusDisabled: boolean;
}>();
const open = defineModel<boolean>('open', { required: true });
const emit = defineEmits<{ select: [modifier: -1 | 1] }>();
const { t } = useI18n();
const popover = useTemplateRef<InstanceType<typeof PositionablePopover>>('popover');
const options = useTemplateRef<HTMLElement>('options');
const position = ref(EPosition.TOP_CENTER);
const arrowStyle = ref<Record<string, string>>({});
const handlePositioned = async (nextPosition: EPosition): Promise<void> => {
  position.value = nextPosition;
  await nextTick();
  arrowStyle.value = getPopoverArrowStyle(props.anchorElement, options.value, 20);
};
const modifiers = [
  {
    value: -1,
    label: 'schoolGrades.worse',
    decimalLabel: 'schoolGrades.decrement',
    activeClass: 'bg-error',
    hoverClass: 'enabled:hover:bg-error/60 enabled:active:bg-error/60',
  },
  {
    value: 1,
    label: 'schoolGrades.better',
    decimalLabel: 'schoolGrades.increment',
    activeClass: 'bg-success',
    hoverClass: 'enabled:hover:bg-success/60 enabled:active:bg-success/60',
  },
] as const;

const handleSelect = (modifier: -1 | 1): void => {
  if (!props.decimalMode) popover.value?.hide();
  emit('select', modifier);
};

const handleHide = async (): Promise<void> => {
  if (!open.value) return;
  const restoreFocus = options.value?.contains(document.activeElement);
  if (restoreFocus) {
    await nextTick();
    // Restore focus before hiding the hover-only trigger.
    props.anchorElement?.focus();
  }
  open.value = false;
};

watch(open, async (active) => {
  if (!active) {
    if (popover.value?.isOpen) popover.value.hide();
    return;
  }
  await popover.value?.show();
  if (!open.value) return;
  await nextTick();
  options.value?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
});
</script>

<template>
  <PositionablePopover
    ref="popover"
    :anchor-element="anchorElement"
    :position="EPosition.TOP_CENTER"
    :offset-y="14"
    :shadow="false"
    role="dialog"
    :aria-label="t('schoolGrades.modifier')"
    flip-strategy="both"
    dialog-style="!overflow-visible !rounded-full shadow-popup"
    content-style="!overflow-visible !rounded-full"
    @positioned="handlePositioned"
    @hide="handleHide"
  >
    <div
      ref="options"
      class="relative z-1 flex items-center justify-center gap-smd px-smd py-smd"
      role="group"
      :aria-label="t('schoolGrades.modifier')"
    >
      <button
        v-for="option in modifiers"
        :key="option.value"
        type="button"
        class="flex size-icon items-center justify-center rounded-full text-white transition-colors focus-visible:outline-2 focus-visible:outline-tint focus-visible:outline-offset-2 disabled:opacity-30 max-md:size-button"
        :class="modifier === option.value ? option.activeClass : ['bg-surface-divider', option.hoverClass]"
        :aria-pressed="modifier === option.value"
        :disabled="option.value === -1 ? minusDisabled : plusDisabled"
        :aria-label="t(decimalMode ? option.decimalLabel : option.label)"
        @click.stop="handleSelect(option.value)"
      >
        <SchoolGradeModifierSymbol :modifier="option.value" class="size-icon-xxs" />
      </button>
    </div>
    <img
      :src="popoverPointer"
      class="pointer-events-none absolute h-icon-md w-icon-sm"
      :style="arrowStyle"
      :class="position === EPosition.TOP_CENTER ? '-bottom-sm' : '-top-sm rotate-180'"
      alt=""
      aria-hidden="true"
    />
  </PositionablePopover>
</template>
